import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Popover } from 'antd';
import axios from 'axios';
import SpotifyApiService from '../../services/SpotifyApiService';
import { SideBarV2Style } from './SideBarV2.style';
import { HomeIcon, SearchIcon, FutureList, GuideMag } from '../../assets/index';
import RecentlyScheduledSidebar from './RecentlyScheduledSidebar';
import { usePlaylistContext } from '../../context/PlaylistContext';
import { useLoginContext } from '../../context';
import { useScheduledSongs } from '../../context';
import { addItemToStorage, getRefreshedToken, truncateText } from '../../utils';
import { fetchAccountUnseenCampaignCount } from '../../services/campaignSubmissionService';
import { getAuthAccountUserId } from '../../utils/authAccount';
import { SPOTIFY_TOKEN } from '../../constants/storage';
import { ENABLE_CURATOR_EARNINGS_DASHBOARD, ENABLE_CURATOR_WALLET } from '../../constants/common';
import CreatePlayListModal from '../../pages/Playlist/CreatePlayListModal';
import PlanToast from '../PlanToast/PlanToast';

interface SideBarV2Props {
  className?: string;
}

const SideBarV2 = React.memo((props: SideBarV2Props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = usePlaylistContext();
  const { state: loginState, dispatch: loginDispatch } = useLoginContext();
  const { setRecentScheduledSongs } = useScheduledSongs();

  // Playlist-related state
  const [playlists, setPlaylists] = useState([]);
  const [totalPlaylist, setTotalPlaylist] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [createPlaylist, setcreatePlaylist] = useState(false);
  const [showPlaylistSearch, setShowPlaylistSearch] = useState(false);
  const [playlistSearchTerm, setPlaylistSearchTerm] = useState('');
  const [searchedPlaylists, setSearchedPlaylists] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoadingMorePlaylists, setIsLoadingMorePlaylists] = useState(false);
  const [totalPlaylistNumber, setTotalPlaylistNumber] = useState(0);
  const [totalPlaylistArr, setTotalPlaylistArr] = useState([]);
  const [totalUnOwnerNumber, setTotalUnOwnerNumber] = useState(0);
  const [isLoadingScheduledSongs, setIsLoadingScheduledSongs] = useState(false);
  const [unseenCampaignsCount, setUnseenCampaignsCount] = useState<number>(0);

  // Function to load playlists with pagination
  const CurrentPages = page => {
    setIsLoadingMorePlaylists(true);
    SpotifyApiService.getInstance()
      .getUserPlaylists(10, playlists.length === totalPlaylist ? 0 : playlists.length)
      .then(genreResponse => {
        setTotalPlaylist(genreResponse?.data.total);
        if (playlists.length === totalPlaylist) {
          setCurrentPage(1);
          setPlaylists([...genreResponse?.data.items]);
        } else {
          setCurrentPage(page + 1);
          setPlaylists([...playlists, ...genreResponse?.data.items]);
        }
        setIsLoadingMorePlaylists(false);
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
        setIsLoadingMorePlaylists(false);
      });
  };

  // Function to get all playlists
  const getPlaylists = offset => {
    SpotifyApiService.getInstance()
      .getUserPlaylists(50, offset)
      .then(genreResponse => {
        setTotalPlaylistArr([...totalPlaylistArr, ...genreResponse?.data.items]);
        if (totalPlaylistNumber < genreResponse?.data.total) {
          setTotalPlaylistNumber(genreResponse?.data.total);
        }
      })
      .catch(async error => {
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
      });
  };

  // Function to search playlists
  const searchPlaylists = async (searchTerm: string) => {
    if (!searchTerm.trim()) {
      setSearchedPlaylists([]);
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await SpotifyApiService.getInstance().searchPlaylists(searchTerm, 20);

      if (response.data.playlists && response.data.playlists.items) {
        const currentUser = JSON.parse(localStorage.getItem('spotifyUser'));
        const userPlaylists = response.data.playlists.items.filter(
          playlist => playlist.owner?.display_name === currentUser?.username
        );
        setSearchedPlaylists(userPlaylists);
      }
    } catch (error) {
      console.error('Error searching playlists:', error);
      if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
        getRefreshedToken(state.refreshToken)
          .then(response => {
            addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
            loginDispatch({
              type: 'ADD_TOKEN',
              payload: response?.data?.access_token,
            });
          })
          .catch(() => {});
      }
    } finally {
      setIsSearching(false);
    }
  };

  // Function to load scheduled songs
  const loadScheduledSongs = () => {
    if (!loginState.token || !loginState.userId) {
      return;
    }

    setIsLoadingScheduledSongs(true);
    const apiUrl = `${process.env.REACT_APP_BACKEND_URI}/GetScheduledList/?page=1&limit=10&access_token=${loginState.token}&userId=${loginState.userId}&preRelease=0&scheduleSong=1`;

    axios(apiUrl, {
      method: 'GET',
      headers: { Authorization: 'Bearer ' + loginState.token },
    })
      .then(response => {
        if (response.data && response.data.data) {
          // Sort by start_time descending (newest first) to match ScheduledTracks page
          const sortedData = response.data.data.sort((a, b) => {
            const aTime =
              a?.localData?.start_time && a?.localData?.start_time === 'null'
                ? new Date().setFullYear(2000)
                : new Date(a?.localData?.start_time).getTime();
            const bTime =
              b?.localData?.start_time && b?.localData?.start_time === 'null'
                ? new Date().setFullYear(2000)
                : new Date(b?.localData?.start_time).getTime();
            return bTime - aTime;
          });
          setRecentScheduledSongs(sortedData);
        }
        setIsLoadingScheduledSongs(false);
      })
      .catch(error => {
        console.error('Failed to load scheduled songs:', error);
        if (error.response?.data?.error?.status === 401 || error.response?.status === 400) {
          getRefreshedToken(state.refreshToken)
            .then(response => {
              addItemToStorage(SPOTIFY_TOKEN, response?.data?.access_token);
              loginDispatch({
                type: 'ADD_TOKEN',
                payload: response?.data?.access_token,
              });
            })
            .catch(() => {});
        }
        setIsLoadingScheduledSongs(false);
      });
  };

  const handleSearchIconClick = () => {
    setShowPlaylistSearch(!showPlaylistSearch);
    setPlaylistSearchTerm('');
    setSearchedPlaylists([]);
    setHasSearched(false);
  };

  // Load playlists on mount
  useEffect(() => {
    if (loginState.token) {
      if (currentPage === 0) {
        CurrentPages(currentPage);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState, currentPage]);

  // Get all playlists
  useEffect(() => {
    if (totalPlaylistArr.length < totalPlaylistNumber || totalPlaylistArr.length === 0) {
      getPlaylists(totalPlaylistArr.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPlaylistNumber, totalPlaylistArr, loginState]);

  // Calculate total owned playlists
  useEffect(() => {
    if (totalPlaylistArr && totalPlaylistArr.length) {
      const spotifyUser = JSON.parse(localStorage.getItem('spotifyUser'));
      let seenUris = new Set<string>();

      let arr = totalPlaylistArr.filter(obj => {
        if (seenUris.has(obj?.uri)) {
          return false;
        }
        seenUris.add(obj?.uri);
        return obj?.owner?.display_name === spotifyUser?.username;
      });

      if (arr.length > 0) {
        setTotalUnOwnerNumber(arr.length);
      }
    }
  }, [totalPlaylistArr, totalPlaylist]);

  // Load scheduled songs when sidebar mounts
  useEffect(() => {
    if (loginState.token && loginState.userId) {
      loadScheduledSongs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState.token, loginState.userId]);

  // Fetch unseen campaigns count
  const fetchUnseenCampaignsCount = async () => {
    try {
      const accountUserId = getAuthAccountUserId();
      if (!accountUserId) return;

      const unseenCount = await fetchAccountUnseenCampaignCount(accountUserId);
      setUnseenCampaignsCount(unseenCount);
    } catch (error) {
      // Silently fail - badge just won't show
    }
  };

  // Fetch unseen count on mount and poll every 5 seconds
  useEffect(() => {
    if (loginState.token && loginState.userId) {
      fetchUnseenCampaignsCount();
      const intervalId = setInterval(fetchUnseenCampaignsCount, 5000);
      return () => clearInterval(intervalId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginState.token, loginState.userId]);

  // Listen for campaignsMarkedAsSeen event (dispatched from CampaignsPage)
  useEffect(() => {
    const handleCampaignsSeen = () => setUnseenCampaignsCount(0);
    window.addEventListener('campaignsMarkedAsSeen', handleCampaignsSeen);
    return () => window.removeEventListener('campaignsMarkedAsSeen', handleCampaignsSeen);
  }, []);

  const menuItems = [
    { title: 'Home', path: '/home', icon: HomeIcon },
    { title: 'Browse', path: '/search', icon: SearchIcon },
    { title: 'Pre-release', path: '/future_playlist', icon: FutureList },
    { title: 'Scheduled tracks', path: '/schedule_tracks', icon: GuideMag },
    { title: 'View campaigns', path: '/campaigns', icon: SearchIcon },
    { title: 'My offers', path: '/curator-offers', icon: SearchIcon },
    // ...(ENABLE_CURATOR_EARNINGS_DASHBOARD
    //   ? [{ title: 'Earnings', path: '/earnings', icon: SearchIcon }]
    //   : []),
    ...(ENABLE_CURATOR_WALLET ? [{ title: 'Wallet', path: '/wallet', icon: SearchIcon }] : []),
  ];

  // recently scheduled items are rendered by the RecentlyScheduled component

  return (
    <SideBarV2Style className={props.className}>
      <div className="sidebar-container">
        {/* Library Section */}
        <div className="library-section">
          <div className="library-header">
            <div className="library-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6C3 4.89543 3.89543 4 5 4H9C10.1046 4 11 4.89543 11 6V18C11 19.1046 10.1046 20 9 20H5C3.89543 20 3 19.1046 3 18V6Z"
                  fill="currentColor"
                />
                <path
                  d="M13 6C13 4.89543 13.8954 4 15 4H19C20.1046 4 21 4.89543 21 6V18C21 19.1046 20.1046 20 19 20H15C13.8954 20 13 19.1046 13 18V6Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <span className="library-title">Menu</span>
          </div>

          {/* Menu Items */}
          <div className="menu-items">
            {menuItems.map((item, index) => {
              // Check if item should be restricted
              const isPreRelease = item.title === 'Pre-release';
              const isScheduledTracks = item.title === 'Scheduled tracks';
              const isCampaigns = item.title === 'View campaigns';
              const isRestricted = isPreRelease || isScheduledTracks;

              const menuContent = (
                <div
                  key={index}
                  className={`menu-item ${location.pathname === item.path ? 'active' : ''}`}
                  onClick={() => navigate(item.path)}
                >
                  {/* <IconComponent color="#ffffff" size={18} /> */}
                  {item.title === 'Scheduled tracks' ? (
                    <span className="menu-item-title">{item.title}</span>
                  ) : item.title === 'Pre-release' ? (
                    <Popover content={<p>View or edit your pre-release songs</p>} placement="right">
                      <span className="menu-item-title">{item.title}</span>
                    </Popover>
                  ) : (
                    <span className="menu-item-title">{item.title}</span>
                  )}
                  {isCampaigns && unseenCampaignsCount > 0 && (
                    <span className="unseen-campaign-badge">{unseenCampaignsCount}</span>
                  )}
                </div>
              );

              // Determine available plans for each restricted item
              const getAvailablePlans = () => {
                if (isPreRelease) return ['unlimited'];
                if (isScheduledTracks) return ['pro', 'unlimited'];
                if (isCampaigns) return ['pro', 'unlimited'];
                return [];
              };

              const getTitleHeading = () => {
                if (isPreRelease) return 'View or edit your pre-release songs';
                if (isScheduledTracks) return 'View or edit your scheduled songs';
                if (isCampaigns) return 'Manage the campaigns you have been selected for';
                return '';
              };

              return isRestricted ? (
                <div key={index} style={{ position: 'relative' }}>
                  <PlanToast
                    available={getAvailablePlans()}
                    titleHeading={getTitleHeading()}
                    showPopup={true}
                    placement="right"
                  >
                    {menuContent}
                  </PlanToast>
                </div>
              ) : (
                menuContent
              );
            })}
          </div>
        </div>

        {/* My Playlists Section */}
        <div className="playlists-section">
          <div className="section-header">
            <span className="section-title">My playlists ({totalUnOwnerNumber})</span>
            <div className="playlist-actions">
              <Popover content={<p>Create playlist</p>} placement="topLeft">
                <span className="action-icon" onClick={() => setcreatePlaylist(true)}>
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 448 512"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M256 80c0-17.7-14.3-32-32-32s-32 14.3-32 32V224H48c-17.7 0-32 14.3-32 32s14.3 32 32 32H192V432c0 17.7 14.3 32 32 32s32-14.3 32-32V288H400c17.7 0 32-14.3 32-32s-14.3-32-32-32H256V80z"></path>
                  </svg>
                </span>
              </Popover>
              {!showPlaylistSearch ? (
                <Popover content={<p>Search playlists</p>} placement="topLeft">
                  <span className="action-icon" onClick={handleSearchIconClick}>
                    <svg
                      stroke="currentColor"
                      fill="currentColor"
                      strokeWidth="0"
                      viewBox="0 0 512 512"
                      height="1em"
                      width="1em"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path>
                    </svg>
                  </span>
                </Popover>
              ) : (
                <span className="action-icon" onClick={handleSearchIconClick}>
                  <svg
                    stroke="currentColor"
                    fill="currentColor"
                    strokeWidth="0"
                    viewBox="0 0 384 512"
                    height="1em"
                    width="1em"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3 297.4 406.6c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256 342.6 150.6z"></path>
                  </svg>
                </span>
              )}
            </div>
          </div>

          {showPlaylistSearch && (
            <div className="search-input-container">
              <input
                type="text"
                placeholder="Search playlists..."
                value={playlistSearchTerm}
                onChange={e => {
                  const value = e.target.value;
                  setPlaylistSearchTerm(value);
                  if (!value.trim()) {
                    setSearchedPlaylists([]);
                    setHasSearched(false);
                  }
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    searchPlaylists(playlistSearchTerm);
                  }
                }}
                className="playlist-search-input"
                autoFocus
              />
              <button onClick={() => searchPlaylists(playlistSearchTerm)} className="search-button">
                <svg
                  className="search-icon"
                  stroke="currentColor"
                  fill="currentColor"
                  strokeWidth="0"
                  viewBox="0 0 512 512"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"></path>
                </svg>
              </button>
            </div>
          )}

          <div className="playlist-items">
            {isSearching && playlistSearchTerm && (
              <div className="search-loading">Searching...</div>
            )}

            {playlistSearchTerm && searchedPlaylists.length > 0 ? (
              searchedPlaylists.map((data, index) => {
                const { name, id } = data;
                return (
                  <div
                    key={id}
                    className={`playlist-item ${location.pathname === `/playlist/${id}` ? 'active' : ''}`}
                    onClick={() => navigate(`/playlist/${id}`)}
                  >
                    <Popover content={<p>{name}</p>} placement="topLeft">
                      <span className="playlist-name">{truncateText(name, 23)}</span>
                    </Popover>
                  </div>
                );
              })
            ) : playlistSearchTerm &&
              !isSearching &&
              searchedPlaylists.length === 0 &&
              hasSearched ? (
              <div className="no-results">No playlists found</div>
            ) : (
              playlists?.map((data, index) => {
                const { name, id } = data;
                const currentUser = JSON.parse(localStorage.getItem('spotifyUser'));
                return data?.owner?.display_name === currentUser?.username ? (
                  <div
                    key={id}
                    className={`playlist-item ${location.pathname === `/playlist/${id}` ? 'active' : ''}`}
                    onClick={() => navigate(`/playlist/${id}`)}
                  >
                    <Popover content={<p>{name}</p>} placement="topLeft">
                      <span className="playlist-name">{truncateText(name, 23)}</span>
                    </Popover>
                  </div>
                ) : null;
              })
            )}
          </div>

          {searchedPlaylists.length > 0
            ? null
            : Number(totalPlaylist) > 10 && (
                <div
                  onClick={e => {
                    if (!isLoadingMorePlaylists) {
                      CurrentPages(currentPage);
                    }
                  }}
                  className="view-more-link"
                  style={{ cursor: isLoadingMorePlaylists ? 'not-allowed' : 'pointer' }}
                >
                  {playlists.length === totalPlaylist ? `View less` : `View more`}
                  {isLoadingMorePlaylists && (
                    <span style={{ marginLeft: '8px', display: 'inline-block' }}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ animation: 'spin 1s linear infinite' }}
                      >
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="2"
                          fill="none"
                          strokeDasharray="31.4 31.4"
                          strokeDashoffset="31.4"
                          style={{ animation: 'dash 1.5s ease-in-out infinite' }}
                        />
                      </svg>
                    </span>
                  )}
                </div>
              )}
        </div>

        {/* Recently Scheduled Section */}
        <div style={{ position: 'relative' }}>
          <PlanToast
            available={['pro', 'unlimited']}
            titleHeading={'Recently Scheduled Songs'}
            showPopup={true}
            placement="right"
          >
            <div className="recently-scheduled-section">
              <RecentlyScheduledSidebar isLoading={isLoadingScheduledSongs} />
            </div>
          </PlanToast>
        </div>
      </div>

      {/* Modals */}
      <CreatePlayListModal
        isModalVisible={createPlaylist}
        setIsModalVisible={setcreatePlaylist}
        playlists={playlists}
        setPlaylists={setPlaylists}
      />
    </SideBarV2Style>
  );
});

export default SideBarV2;
